// FO-305 P3 (batch-33) — payroll-uren-correctielaag: DTOs + gedeeld Zod-schema
// (API én backoffice-form gebruiken hetzelfde schema zodat het form niets kan
// sturen dat de API weigert). Een correctie is een verloning-CLASSIFICATIE: ze
// verplaatst uren tussen "potjes" en wijzigt NOOIT de geregistreerde uren of de
// werkbon (de weekstaat blijft de uren-waarheid).

import { z } from 'zod'

/** De potjes waartussen uren verplaatst kunnen worden. Spiegelt de Prisma-enum. */
export const PAYROLL_UREN_POTJES = [
  'VERREKEND',
  'UITBETALEN',
  'PLOEG_2',
  'PLOEG_3',
  'PLOEG_5',
  'REGULIER',
  'OVERWERK_100',
  'OVERWERK_125',
  'OVERWERK_150',
  'OVERWERK_200',
] as const
export type PayrollUrenPotje = (typeof PAYROLL_UREN_POTJES)[number]

export const PAYROLL_UREN_POTJE_LABELS: Record<PayrollUrenPotje, string> = {
  VERREKEND: 'Verrekend (contracturen)',
  UITBETALEN: 'Uitbetalen (mutatie)',
  PLOEG_2: '2-ploegen',
  PLOEG_3: '3-ploegen',
  PLOEG_5: '5-ploegen',
  REGULIER: 'Regulier (geen ploeg)',
  OVERWERK_100: 'Meeruren 100%',
  OVERWERK_125: 'Overuren 125%',
  OVERWERK_150: 'Overuren 150%',
  OVERWERK_200: 'Overuren 200%',
}

/** Eén correctie-regel (audit-rij) zoals getoond onder de medewerker. */
export interface PayrollUrenCorrectieDTO {
  id: string
  employeeId: string
  jaar: number
  maand: number
  bronPotje: PayrollUrenPotje
  doelPotje: PayrollUrenPotje
  uren: number
  reden: string
  createdById: string
  createdAt: string
  /** Gevuld wanneer deze rij een tegenboeking van een andere correctie is. */
  tegenboekingVanId: string | null
  /** True als deze correctie inmiddels door een tegenboeking is geneutraliseerd. */
  isGetegenboekt: boolean
}

/** Het saldo per potje voor een medewerker/maand (bovengrens van een correctie). */
export interface PayrollUrenSaldiDTO {
  employeeId: string
  jaar: number
  maand: number
  saldi: Record<PayrollUrenPotje, number>
  /** True zodra de maand vergrendeld is (geen correcties meer mogelijk). */
  maandVergrendeld: boolean
}

const yearSchema = z.coerce.number().int().min(2024).max(2100)
const monthSchema = z.coerce.number().int().min(1).max(12)

/** uren > 0, veelvoud van 0,25. */
const urenSchema = z
  .number()
  .positive('Aantal uren moet groter dan 0 zijn')
  .refine((u) => Math.round(u * 100) % 25 === 0, {
    message: 'Aantal uren moet een veelvoud van 0,25 zijn',
  })

export const createPayrollUrenCorrectieSchema = z
  .object({
    employeeId: z.string().min(1),
    jaar: yearSchema,
    maand: monthSchema,
    bronPotje: z.enum(PAYROLL_UREN_POTJES),
    doelPotje: z.enum(PAYROLL_UREN_POTJES),
    uren: urenSchema,
    reden: z.string().trim().min(5, 'Reden is verplicht (min. 5 tekens)').max(500),
  })
  .refine((d) => d.bronPotje !== d.doelPotje, {
    message: 'Bron- en doel-potje mogen niet gelijk zijn',
    path: ['doelPotje'],
  })
export type CreatePayrollUrenCorrectieInput = z.infer<typeof createPayrollUrenCorrectieSchema>

/** Query voor de correctie-lijst + saldi van één medewerker/maand. */
export const payrollUrenCorrectieQuerySchema = z.object({
  employeeId: z.string().min(1),
  jaar: yearSchema,
  maand: monthSchema,
})
export type PayrollUrenCorrectieQuery = z.infer<typeof payrollUrenCorrectieQuerySchema>
